import React, { useState, useRef, useEffect } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  Calendar,
  Users,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import { convertScheduleToClientTimezone } from "../helpers/convertScheduleToClientTimezone";

const UserScheduleBooking = ({
  availableSchedules,
  onScheduleSelect,
  className = "",
  selectedSchedule,
}) => {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const timeSlotsRef = useRef(null);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // // Reset selection when availableSchedules change
  // useEffect(() => {
  //   if (selectedDate && selectedSchedule) {
  //     const dateKey = formatDateKey(selectedDate);
  //     const schedules = availableSchedules?.[dateKey] || [];
  //     const stillExists = schedules.find((s) => s.id === selectedSchedule.id);

  //     if (!stillExists) {
  //       onScheduleSelect?.(null, null);
  //       setSelectedDate(null);
  //     }
  //   }
  // }, [availableSchedules]);

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
  };

  const formatDateKey = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(date.getDate()).padStart(2, "0")}`;
  };

  const parseTime = (timeStr) => {
    const [hours, minutes] = timeStr.split(":").map(Number);
    return hours * 60 + minutes;
  };

  const changeMonth = (offset) => {
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + offset, 1)
    );
  };

  const selectDate = (day) => {
    const newSelectedDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    setSelectedDate(newSelectedDate);

    // Clear selected schedule when changing date
    if (onScheduleSelect) {
      onScheduleSelect(null, null);
    }

    setTimeout(() => {
      if (timeSlotsRef.current) {
        timeSlotsRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 100);
  };

  const selectSchedule = (schedule) => {
    if (onScheduleSelect) {
      onScheduleSelect(schedule, formatDateKey(selectedDate));
    }
  };

  const isToday = (day) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      currentDate.getMonth() === today.getMonth() &&
      currentDate.getFullYear() === today.getFullYear()
    );
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      day === selectedDate.getDate() &&
      currentDate.getMonth() === selectedDate.getMonth() &&
      currentDate.getFullYear() === selectedDate.getFullYear()
    );
  };

  const hasAvailableSlots = (day) => {
    const dateKey = formatDateKey(
      new Date(currentDate.getFullYear(), currentDate.getMonth(), day)
    );
    const schedules = availableSchedules?.[dateKey];
    return (
      schedules &&
      schedules.length > 0 &&
      schedules.some((s) => s.availableSlots > 0)
    );
  };

  const isPastDate = (day) => {
    const checkDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    checkDate.setHours(0, 0, 0, 0);
    return checkDate < today;
  };

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentDate);
    const firstDay = getFirstDayOfMonth(currentDate);
    const days = [];

    for (let i = 0; i < firstDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-12"></div>);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const past = isPastDate(day);
      const available = hasAvailableSlots(day);
      const disabled = past || !available;

      days.push(
        <button
          key={day}
          onClick={() => !disabled && selectDate(day)}
          disabled={disabled}
          className={`h-12 rounded-lg font-medium transition-all relative
            ${
              isSelected(day)
                ? "bg-blue-600 text-white shadow-lg scale-105"
                : isToday(day) && available
                ? "bg-blue-100 text-blue-700 hover:bg-blue-200"
                : disabled
                ? "text-gray-300 cursor-not-allowed"
                : "hover:bg-gray-100 text-gray-700"
            }`}
        >
          {day}
          {available && !past && (
            <div className="absolute w-1 h-1 transform -translate-x-1/2 bg-green-500 rounded-full bottom-1 left-1/2"></div>
          )}
        </button>
      );
    }

    return days;
  };

  const selectedDateKey = selectedDate ? formatDateKey(selectedDate) : null;
  const selectedDateSchedules = selectedDateKey
    ? availableSchedules?.[selectedDateKey] || []
    : [];
  const availableSchedulesForDate = selectedDateSchedules.filter(
    (s) => s.availableSlots > 0
  );

  return (
    <div className={className}>
      <div className="grid gap-6 md:grid-cols-2">
        {/* Calendar Component */}
        <div className="p-6 bg-white shadow-lg rounded-2xl">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-800">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={() => changeMonth(-1)}
                className="p-2 transition rounded-lg hover:bg-gray-100"
                aria-label="Previous month"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>
              <button
                onClick={() => changeMonth(1)}
                className="p-2 transition rounded-lg hover:bg-gray-100"
                aria-label="Next month"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>
          </div>

          <div className="grid grid-cols-7 gap-2 mb-2">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((day) => (
              <div
                key={day}
                className="flex items-center justify-center h-8 text-sm font-semibold text-center text-gray-600"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-2">{renderCalendar()}</div>

          <div className="flex items-center gap-4 mt-4 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-blue-600 rounded"></div>
              <span>Selected</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-green-500 rounded-full"></div>
              <span>Available</span>
            </div>
          </div>
        </div>

        {/* Time Slots Component */}
        <div ref={timeSlotsRef} className="p-6 bg-white shadow-lg rounded-2xl">
          {!selectedDate ? (
            <div className="py-16 text-center">
              <Calendar className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="mb-2 text-lg font-semibold text-gray-400">
                Select a Date
              </h3>
              <p className="text-gray-500">
                Choose a date from the calendar to see available time slots
              </p>
            </div>
          ) : (
            <>
              <div className="mb-6">
                <h2 className="text-xl font-bold text-gray-800">
                  Available Time Slots
                </h2>
                <p className="mt-1 text-sm text-gray-600">
                  {`${selectedDate.toLocaleDateString("en-US", {
                    weekday: "long",
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })} ${
                    -new Date().getTimezoneOffset() / 60 === 7 ? "" : "(UTC+7)"
                  }`}
                </p>
              </div>

              {availableSchedulesForDate.length === 0 ? (
                <div className="py-12 text-center text-gray-400">
                  <AlertCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p className="font-medium">No available slots</p>
                  <p className="mt-1 text-sm">Please select another date</p>
                </div>
              ) : (
                <div className="space-y-3 overflow-y-auto max-h-96">
                  {availableSchedulesForDate.map((schedule) => {
                    const duration =
                      parseTime(schedule.end) - parseTime(schedule.start);

                    const isSelectedSchedule = selectedSchedule.find(
                      (s) => s.id === schedule.id
                    );

                    return (
                      <button
                        key={schedule.id}
                        onClick={() => selectSchedule(schedule)}
                        className={`w-full p-4 rounded-lg border-2 transition-all text-left
                          ${
                            isSelectedSchedule
                              ? "border-blue-600 bg-blue-50 shadow-md"
                              : `border-gray-200 ${
                                  schedule.availableSlots > 0
                                    ? "hover:border-blue-300 hover:bg-gray-50"
                                    : "cursor-not-allowed"
                                }`
                          }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center flex-1 gap-3">
                            <Clock
                              className={`w-5 h-5 flex-shrink-0 ${
                                isSelectedSchedule
                                  ? "text-blue-600"
                                  : "text-gray-600"
                              }`}
                            />
                            <div>
                              <div>
                                <div
                                  className={`font-semibold ${
                                    isSelectedSchedule
                                      ? "text-blue-900"
                                      : "text-gray-800"
                                  }`}
                                >
                                  {schedule.start} - {schedule.end}
                                </div>
                                <div className="text-xs text-gray-600">
                                  {convertScheduleToClientTimezone({
                                    ...schedule,
                                    date: new Intl.DateTimeFormat(
                                      "en-CA"
                                    ).format(new Date(selectedDate)), // turn selected date into yyyy-mm-dd format
                                    isDate: true,
                                    isHour: true,
                                  })}
                                </div>
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-sm text-gray-600">
                                <span>
                                  Duration: {Math.floor(duration / 60)}h{" "}
                                  {duration % 60}m
                                </span>
                                <div className="flex items-center gap-1">
                                  <Users className="w-3.5 h-3.5" />
                                  <span>
                                    {schedule.availableSlots} slot
                                    {schedule.availableSlots !== 1
                                      ? "s"
                                      : ""}{" "}
                                    left
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                          {isSelectedSchedule && (
                            <CheckCircle className="flex-shrink-0 w-6 h-6 text-blue-600" />
                          )}
                        </div>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserScheduleBooking;
